import { useEffect, useRef } from "react";
import { Pencil, Plus } from "lucide-react";
import type { CurvaDocument } from "@/document/model/document";
import type { DocumentStyle, DocumentStyleKind } from "@/document/model/documentStyles";
import { listDocumentStylesByKind } from "@/document/model/documentStyles";
import { DraggablePanel } from "./DraggablePanel";
import { documentStyleKindLabel, documentStyleLabel, documentStylePreviewBackground } from "./stylePreview";

export function StylePickerPopover({
  document,
  kind,
  onSelect,
  onCreate,
  onEdit,
  onClose,
}: {
  document: CurvaDocument;
  kind: DocumentStyleKind;
  onSelect: (styleId: string) => void;
  onCreate: () => void;
  onEdit: (styleId: string) => void;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const styles = listDocumentStylesByKind(document, kind);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || panelRef.current?.contains(target)) {
        return;
      }
      onClose();
    }

    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [onClose]);

  return (
    <DraggablePanel
      panelRef={panelRef}
      storageKey={`style-picker-${kind}`}
      title={`${documentStyleKindLabel(kind)} styles`}
      widthClass="w-64"
      onClose={onClose}
    >
      <div className="space-y-2">
        <button
          type="button"
          onClick={onCreate}
          className="flex h-8 w-full items-center justify-center gap-1 rounded border border-dashed border-white/15 text-[11px] text-slate-300 hover:border-white/25 hover:bg-white/[0.04]"
        >
          <Plus className="size-3.5" />
          Create style
        </button>
        {styles.length === 0 ? (
          <div className="rounded bg-white/[0.03] px-2 py-3 text-center text-[11px] text-slate-500">
            No {documentStyleKindLabel(kind).toLowerCase()} styles yet.
          </div>
        ) : (
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {styles.map(style => (
              <StylePickerRow
                key={style.id}
                document={document}
                style={style}
                onSelect={() => onSelect(style.id)}
                onEdit={() => onEdit(style.id)}
              />
            ))}
          </div>
        )}
      </div>
    </DraggablePanel>
  );
}

function StylePickerRow({
  document,
  style,
  onSelect,
  onEdit,
}: {
  document: CurvaDocument;
  style: DocumentStyle;
  onSelect: () => void;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-white/[0.06]">
      <button
        type="button"
        onClick={onSelect}
        className="flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-1 text-left"
      >
        <span
          className="size-5 shrink-0 rounded-sm border border-white/20"
          style={{ background: documentStylePreviewBackground(document, style) }}
        />
        <span className="min-w-0 truncate text-[12px] text-slate-200">{documentStyleLabel(style)}</span>
      </button>
      <button
        type="button"
        aria-label={`Edit ${documentStyleLabel(style)}`}
        onClick={onEdit}
        className="grid size-7 shrink-0 place-items-center rounded text-slate-400 hover:bg-white/[0.08] hover:text-slate-100"
      >
        <Pencil className="size-3.5" />
      </button>
    </div>
  );
}
